import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Check,
  ChevronRight,
  CircleAlert,
  Clock3,
  Inbox,
  MessageSquareText,
  RotateCcw,
} from "lucide-react";
import { api } from "../../lib/api";
import {
  AdminPageHeader,
  Button,
  Callout,
  Metric,
  Panel,
  Select,
  Textarea,
  errorMessage,
  formatDate,
  formatMoney,
  humanize,
} from "./client-operations-shared";

type InboxSeverity = "critical" | "warning" | "info";
type ChaseState = "none" | "reminder_sent" | "promise_to_pay" | "escalated";

type InboxItem = {
  fingerprint: string;
  kind: string;
  severity: InboxSeverity;
  companyId: string | null;
  companyName: string | null;
  paddleTransactionId: string | null;
  title: string;
  detail: string;
  href: string;
  occurredAt: string | null;
  dueAt: string | null;
  amountMinor: number | null;
  currency: string | null;
  acknowledged: boolean;
  acknowledgedAt: string | null;
  chaseState: ChaseState;
  note: string | null;
};

type InboxResponse = {
  generatedAt: string;
  paddleConfigured: boolean;
  liveHealthError: string | null;
  summary: {
    critical: number;
    warning: number;
    info: number;
    acknowledged: number;
    visible: number;
    total: number;
  };
  items: InboxItem[];
};

type StatePatch = {
  acknowledged?: boolean;
  chaseState?: ChaseState;
  note?: string | null;
};

const SEVERITY_COPY: Record<InboxSeverity, { label: string; description: string }> = {
  critical: { label: "Act now", description: "Payment or platform conditions that need a decision." },
  warning: { label: "Review next", description: "Upcoming changes and commercial follow-up." },
  info: { label: "On track", description: "Open work that is still inside its expected window." },
};

export function InboxWorkspace() {
  const [includeAcknowledged, setIncludeAcknowledged] = useState(false);
  const queryClient = useQueryClient();
  const inboxQ = useQuery({
    queryKey: ["admin", "inbox", includeAcknowledged],
    queryFn: () => api<InboxResponse>(`/admin/inbox?includeAcknowledged=${includeAcknowledged}`),
    staleTime: 60_000,
  });
  const updateState = useMutation({
    mutationFn: ({ fingerprint, patch }: { fingerprint: string; patch: StatePatch }) =>
      api<{ item: InboxItem }>(`/admin/inbox/${encodeURIComponent(fingerprint)}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin", "inbox"] }),
  });

  const data = inboxQ.data;
  const items = data?.items ?? [];
  const groups = (["critical", "warning", "info"] as const)
    .map((severity) => ({ severity, items: items.filter((item) => item.severity === severity) }))
    .filter((group) => group.items.length > 0);

  return (
    <div className="space-y-5">
      <AdminPageHeader
        eyebrow="Daily control room"
        title="Inbox"
        description="One prioritized worklist for unpaid invoices, onboarding follow-up, renewals, usage review, and Paddle exceptions. Nothing here changes access automatically."
        actions={
          <label className="inline-flex min-h-9 items-center gap-2 rounded-md border border-border px-3 text-xs text-muted">
            <input
              type="checkbox"
              checked={includeAcknowledged}
              onChange={(event) => setIncludeAcknowledged(event.target.checked)}
            />
            Show handled
          </label>
        }
      />

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Metric label="Act now" value={data?.summary.critical ?? "—"} tone={data?.summary.critical ? "danger" : "neutral"} detail="Unacknowledged critical items" />
        <Metric label="Review next" value={data?.summary.warning ?? "—"} tone={data?.summary.warning ? "warning" : "neutral"} detail="Unacknowledged warnings" />
        <Metric label="On track" value={data?.summary.info ?? "—"} detail="Open inside expected window" />
        <Metric label="Handled" value={data?.summary.acknowledged ?? "—"} tone="good" detail="Acknowledged while still active" />
      </div>

      {data?.liveHealthError && (
        <Callout tone="danger" title="Live Paddle comparison is unavailable">
          Local billing facts are shown, but live drift could not be checked. Open Billing health before treating the queue as reconciled.
        </Callout>
      )}
      {!data?.paddleConfigured && !inboxQ.isLoading && (
        <Callout tone="warning" title="Paddle is not configured in this environment">
          Local lifecycle and invoice facts remain visible; live Paddle drift and webhook health do not.
        </Callout>
      )}

      {inboxQ.isLoading ? (
        <Panel><div className="p-10 text-center text-sm text-muted">Assembling the operational worklist…</div></Panel>
      ) : inboxQ.isError ? (
        <Callout tone="danger" title="Inbox could not be loaded">{errorMessage(inboxQ.error)}</Callout>
      ) : items.length === 0 ? (
        <Panel>
          <div className="p-12 text-center">
            <Inbox size={28} className="mx-auto text-posGreen" aria-hidden="true" />
            <div className="mt-3 text-sm font-semibold">No open items in this view</div>
            <p className="mt-1 text-xs text-muted">
              {includeAcknowledged ? "There are no active operational flags." : "All active items have been handled."}
            </p>
          </div>
        </Panel>
      ) : (
        <div className="space-y-4">
          {groups.map((group) => (
            <Panel
              key={group.severity}
              title={`${SEVERITY_COPY[group.severity].label} · ${group.items.length}`}
              description={SEVERITY_COPY[group.severity].description}
            >
              <div className="divide-y divide-border/70">
                {group.items.map((item) => (
                  <InboxRow
                    key={item.fingerprint}
                    item={item}
                    pending={updateState.isPending && updateState.variables?.fingerprint === item.fingerprint}
                    onUpdate={(patch) => updateState.mutate({ fingerprint: item.fingerprint, patch })}
                  />
                ))}
              </div>
            </Panel>
          ))}
          {updateState.isError && (
            <Callout tone="danger" title="The Inbox update was not saved">{errorMessage(updateState.error)}</Callout>
          )}
        </div>
      )}
    </div>
  );
}

function InboxRow({ item, pending, onUpdate }: {
  item: InboxItem;
  pending: boolean;
  onUpdate: (patch: StatePatch) => void;
}) {
  const [note, setNote] = useState(item.note ?? "");
  const invoiceItem = item.kind.startsWith("invoice_");
  const Icon = item.severity === "critical" ? CircleAlert : item.severity === "warning" ? AlertTriangle : Clock3;
  return (
    <article className={item.acknowledged ? "bg-border/5 px-4 py-4 opacity-70" : "px-4 py-4"}>
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-start">
        <div className="flex min-w-0 gap-3">
          <span className={severityIconClass(item.severity)}><Icon size={16} aria-hidden="true" /></span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="text-xs font-semibold text-fg">{item.companyName ?? "Platform operations"}</span>
              <span className="text-[10px] uppercase tracking-wide text-muted">{humanize(item.kind)}</span>
              {item.acknowledged && <span className="rounded-full bg-posGreen/10 px-2 py-0.5 text-[10px] font-medium text-posGreen">Handled</span>}
            </div>
            <h3 className="mt-1 text-sm font-medium text-fg">{item.title}</h3>
            <p className="mt-1 max-w-3xl text-xs leading-5 text-muted">{item.detail}</p>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted">
              {item.amountMinor != null && <span className="font-medium tabular-nums text-fg">{formatMoney(item.amountMinor, item.currency ?? "GBP")}</span>}
              {item.dueAt && <span>Due {formatDate(item.dueAt)} · {relativeDate(item.dueAt)}</span>}
              {!item.dueAt && item.occurredAt && <span>Raised {formatDate(item.occurredAt, true)}</span>}
              {item.note && <span className="inline-flex items-center gap-1"><MessageSquareText size={11} /> Internal note saved</span>}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 lg:justify-end">
          {invoiceItem && (
            <Select
              aria-label={`Chase state for ${item.companyName ?? item.title}`}
              className="w-40 text-xs"
              value={item.chaseState}
              disabled={pending}
              onChange={(event) => onUpdate({ chaseState: event.target.value as ChaseState })}
            >
              <option value="none">No chase action</option>
              <option value="reminder_sent">Reminder sent</option>
              <option value="promise_to_pay">Promise to pay</option>
              <option value="escalated">Escalated</option>
            </Select>
          )}
          <a href={item.href} className="inline-flex min-h-8 items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-fg hover:border-accent/50">
            Open {item.companyId ? "client" : "health"}<ChevronRight size={13} />
          </a>
          <Button
            variant={item.acknowledged ? "quiet" : "secondary"}
            disabled={pending}
            onClick={() => onUpdate({ acknowledged: !item.acknowledged })}
          >
            {item.acknowledged ? <><RotateCcw size={13} /> Reopen</> : <><Check size={13} /> Handled</>}
          </Button>
        </div>
      </div>
      <details className="ml-9 mt-3">
        <summary className="cursor-pointer text-[11px] text-muted hover:text-fg">Internal follow-up note</summary>
        <div className="mt-2 flex max-w-2xl items-end gap-2">
          <Textarea
            className="min-h-16 text-xs"
            value={note}
            maxLength={4000}
            placeholder="Context for the next operator. Never paste payment credentials."
            onChange={(event) => setNote(event.target.value)}
          />
          <Button disabled={pending || note === (item.note ?? "")} onClick={() => onUpdate({ note: note.trim() || null })}>Save note</Button>
        </div>
      </details>
    </article>
  );
}

function severityIconClass(severity: InboxSeverity) {
  if (severity === "critical") return "mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-negRed/10 text-negRed";
  if (severity === "warning") return "mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-warnOrange/10 text-warnOrange";
  return "mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent/10 text-accent";
}

function relativeDate(value: string) {
  const date = new Date(value);
  const milliseconds = date.getTime() - Date.now();
  const days = Math.ceil(milliseconds / 86_400_000);
  // The server-owned title carries the authoritative age. Keep this secondary
  // label qualitative so client/server clocks and partial days cannot disagree.
  if (milliseconds < 0) return "overdue";
  if (days === 0) return "due today";
  return `in ${days}d`;
}
